import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Button, Dropdown, Form } from '@bynder/design-system';
import { fontsElementsSelector } from 'packages/store/creativeEditor/creativeEditor.selectors';
import generateTestId from 'packages/helpers/testIdHelpers';
import type { FontFamily as FontFamilyType } from '../../Shots/Text/Fonts/types';

type Props = {
    fontStyleData: [string, boolean, string];
    fontId: string;
    onFontStyleChange: (fontId: string) => void;
    disabled?: boolean;
    locked?: boolean;
};

const FontStyleBase = ({ fontStyleData, fontId, onFontStyleChange, disabled, locked }: Props) => {
    const [currentFontId, isMixed, mixedValue] = fontStyleData;
    const fonts: FontFamilyType[] = useSelector(fontsElementsSelector);

    const { familyFonts, currentStyleName } = useMemo(() => {
        let matchedFonts: { id: number; name: string; style: string }[] = [];
        let styleName = '';

        for (const family of fonts) {
            const match = family.fonts.find((f) => String(f.id) === fontId);

            if (match) {
                matchedFonts = family.fonts;
                const current = family.fonts.find((f) => String(f.id) === currentFontId);
                styleName = current?.name || match.name;

                break;
            }
        }

        return { familyFonts: matchedFonts, currentStyleName: styleName };
    }, [fonts, fontId, currentFontId]);

    const displayValue = isMixed ? mixedValue : currentStyleName;

    const handleChange = useCallback(
        (font: { id: number }) => {
            onFontStyleChange(String(font.id));
        },
        [onFontStyleChange],
    );

    return (
        <Form.Group>
            <Dropdown
                {...generateTestId('shots_text_style')}
                trigger={({ isOpen, ...triggerProps }) => (
                    <Button
                        aria-label="shots_text_style"
                        isPressed={isOpen}
                        isDisabled={disabled || locked || familyFonts.length <= 1}
                        rightIcon={<Dropdown.Arrow />}
                        isFullWidth
                        {...triggerProps}
                    >
                        <Dropdown.SelectButtonText>{displayValue}</Dropdown.SelectButtonText>
                    </Button>
                )}
                position="bottom"
            >
                {familyFonts.map((font) => (
                    <Dropdown.Item
                        key={font.id}
                        isChecked={String(font.id) === currentFontId}
                        onClick={() => handleChange(font)}
                    >
                        {font.name}
                    </Dropdown.Item>
                ))}
            </Dropdown>
        </Form.Group>
    );
};

export { FontStyleBase };
